import React from "react";
import {
  Box,
  Button,
  ColumnLayout,
  Container,
  Header,
  SpaceBetween,
  Spinner,
  SplitPanel,
} from "@amzn/awsui-components-react";
import { FilePreview } from "components/file-preview";
import { isFileTypePreviewable } from "components/file-preview/utils";
import { useFilePreviewSplitPanel } from "./hooks";
import { getIngestionStatusIndicator } from "components/atlas-documents-table/column-definitions";
import {
  useDownloadUrlQuery,
  useLazyDownloadUrlQuery,
} from "components/documents-table-os/endpoints";
import { useListAtlasDocumentsQuery } from "pages/gaia-ai/endpoint";
import { useAppSelector } from "app/store";
import { useSelectedTeam } from "features/team-settings/hooks";
import { ValueWithLabel } from "utils/value-with-label";
import { datePopOver } from "utils/date-format";

const AtlasFilePreviewPanel: React.FC = () => {
  const { splitPanel } = useFilePreviewSplitPanel();
  const documentId = splitPanel.selectedDocumentId;
  const teamName = useAppSelector((state) => state.audits.teamName);
  const { isKBApprovalEnabled } = useSelectedTeam();

  // Read from cached atlas documents query
  const { data: atlasData } = useListAtlasDocumentsQuery(
    { teamName, includeApprovalInfo: isKBApprovalEnabled },
    { skip: !teamName },
  );

  const atlasDoc = atlasData?.documents?.find(
    (d) => d.documentUrl === documentId,
  );

  // Derive file info from the documentId (S3 path) or cached doc
  const fileNameWithExt = documentId?.split("/").pop() || "";
  const extensionType = fileNameWithExt.includes(".")
    ? `.${fileNameWithExt.split(".").pop()?.toLowerCase()}`
    : "";

  const { data: downloadUrl, isLoading } = useDownloadUrlQuery(
    { documentId: documentId ?? "", preview: true },
    { skip: !documentId, refetchOnMountOrArgChange: true },
  );

  const [fetchDownloadUrl] = useLazyDownloadUrlQuery();

  const handleDownload = async () => {
    if (!documentId) return;
    try {
      const result = await fetchDownloadUrl({
        documentId,
        preview: false,
      }).unwrap();
      if (result?.url) {
        window.location.href = result.url;
      }
    } catch {
      // fallback to preview URL
      if (downloadUrl?.url) {
        window.location.href = downloadUrl.url;
      }
    }
  };

  const handleOpenInNewTab = () => {
    if (downloadUrl?.url) {
      window.open(downloadUrl.url, "_blank");
    }
  };

  if (!documentId) {
    return (
      <SplitPanel
        header="File Preview"
        i18nStrings={{
          closeButtonAriaLabel: "Close panel",
          openButtonAriaLabel: "Open panel",
        }}
      >
        <Box textAlign="center" padding="l">
          Select a file to preview
        </Box>
      </SplitPanel>
    );
  }

  return (
    <SplitPanel
      header={decodeURIComponent(fileNameWithExt)}
      i18nStrings={{
        closeButtonAriaLabel: "Close panel",
        openButtonAriaLabel: "Open panel",
      }}
      hidePreferencesButton
    >
      {isLoading ? (
        <Container>
          <Box textAlign="center">
            <Spinner /> Loading preview...
          </Box>
        </Container>
      ) : (
        <SpaceBetween direction="vertical" size="l">
          <Container
            header={
              <Header
                variant="h2"
                actions={
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button
                      onClick={handleOpenInNewTab}
                      disabled={!isFileTypePreviewable(extensionType)}
                    >
                      Open in new tab
                    </Button>
                    <Button onClick={handleDownload} variant="primary">
                      Download
                    </Button>
                  </SpaceBetween>
                }
              >
                Details
              </Header>
            }
          >
            <ColumnLayout columns={2} variant="text-grid">
              <SpaceBetween direction="vertical" size="l">
                <ValueWithLabel label="File size">
                  {atlasDoc?.size || "-"}
                </ValueWithLabel>
                <ValueWithLabel label="Ingestion status">
                  {getIngestionStatusIndicator(atlasDoc?.ingestionStatus)}
                </ValueWithLabel>
              </SpaceBetween>
              <SpaceBetween direction="vertical" size="l">
                <ValueWithLabel label="Uploaded at">
                  {atlasDoc?.createdAt
                    ? datePopOver(Number(atlasDoc.createdAt))
                    : "-"}
                </ValueWithLabel>
                <ValueWithLabel label="Uploaded by">
                  {atlasDoc?.createdBy || "-"}
                </ValueWithLabel>
              </SpaceBetween>
            </ColumnLayout>
          </Container>

          <Container header={<Header variant="h2">Preview</Header>}>
            {downloadUrl?.url && fileNameWithExt ? (
              <FilePreview
                type={extensionType}
                fileName={fileNameWithExt}
                presignedUrl={downloadUrl.url}
              />
            ) : (
              <Box textAlign="center">Preview not available</Box>
            )}
          </Container>
        </SpaceBetween>
      )}
    </SplitPanel>
  );
};

export default AtlasFilePreviewPanel;
